import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip,
} from '@mui/material'
import { ArrowUpward, ArrowDownward, DragHandle, Close } from '@mui/icons-material'
import { formatNumber, formatPercent } from '../../utils/format'

const METRICS = [
  { key: 'capacityUtilL1', label: 'Line 1 Capacity', format: formatPercent, lowerBetter: true, unit: '%' },
  { key: 'capacityUtilL2', label: 'Line 2 Capacity', format: formatPercent, lowerBetter: true, unit: '%' },
  { key: 'capacityUtilL3', label: 'Line 3 Capacity', format: formatPercent, lowerBetter: true, unit: '%' },
  { key: 'belowMinDOS', label: 'Items Below Min DOS', format: formatNumber, lowerBetter: true },
  { key: 'weightedAvgDOS', label: 'Weighted Avg DOS', format: (v) => v?.toFixed(1), lowerBetter: false },
  { key: 'totalCases', label: 'Total Cases', format: formatNumber, lowerBetter: false },
  { key: 'totalECs', label: 'Total Equiv. Cases', format: formatNumber, lowerBetter: false },
  { key: 'budgetVariance', label: 'Budget Variance', format: formatPercent, lowerBetter: true, unit: '%' },
]

function DeltaCell({ a, b, lowerBetter }) {
  if (a == null || b == null) return <TableCell>—</TableCell>

  const delta = b - a
  if (Math.abs(delta) < 0.01) {
    return (
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <DragHandle sx={{ fontSize: 16 }} />
          <Typography variant="body2">0</Typography>
        </Box>
      </TableCell>
    )
  }

  const isIncrease = delta > 0
  const isBetter = lowerBetter ? !isIncrease : isIncrease
  const color = isBetter ? 'success.main' : 'error.main'
  const Icon = isIncrease ? ArrowUpward : ArrowDownward
  const sign = isIncrease ? '+' : ''

  return (
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
          {sign}{typeof delta === 'number' && Math.abs(delta) < 1 ? delta.toFixed(1) : formatNumber(delta)}
        </Typography>
      </Box>
    </TableCell>
  )
}

export default function CompareView({ scenarioA, scenarioB, onClose }) {
  if (!scenarioA || !scenarioB) return null

  const mA = scenarioA.compareMetrics || {}
  const mB = scenarioB.compareMetrics || {}

  return (
    <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Scenario Comparison
          </Typography>
          <Tooltip title="Close comparison">
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip
            label={scenarioA.name}
            color="primary"
            variant="outlined"
            sx={{ flex: 1, justifyContent: 'center' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>vs</Typography>
          <Chip
            label={scenarioB.name}
            color="warning"
            variant="outlined"
            sx={{ flex: 1, justifyContent: 'center' }}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{scenarioA.name}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{scenarioB.name}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Delta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {METRICS.map(({ key, label, format, lowerBetter }) => (
                <TableRow key={key} hover>
                  <TableCell>
                    <Typography variant="body2">{label}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{format(mA[key])}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{format(mB[key])}</Typography>
                  </TableCell>
                  <DeltaCell a={mA[key]} b={mB[key]} lowerBetter={lowerBetter} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}
